import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Typography,
} from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { useBeforeUnload, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  useAvailableReportPlatformTypes,
  useDeleteReportBuilderMutation,
  useDeleteReportBuilderScheduleMutation,
  useGenerateReportBuilderDownloadMutation,
  useGenerateReportBuilderPreviewMutation,
  useRecipients,
  useReportBuilder,
  useReportBuilderHomeList,
  useReportBuilderOptions,
  useReportBuilderSchedule,
  useSaveReportBuilderMutation,
  useSaveReportBuilderScheduleMutation,
} from '../../hooks/useReports'
import { useAuthSession } from '../../lib/session'
import { useToast } from '../../contexts/ToastContext'
import { permissions, UserTypes } from '../../types/authorization'
import type { FieldOption, ReportBuilder, ReportBuilderPreviewRequest, ReportBuilderSchedule } from '../../types/reports'
import { useAuthorization } from '../../contexts/AuthorizationContext'
import { getSelectedUtcTime, revertUtcTime } from '../../features/reports/schedule/time'
import { PLATFORM_TYPE_OPTIONS } from './reportOptionConstants'
import { entityNameSchema, longTextSchema, shortTextSchema, zodAntdValidator } from '../../lib/validation'

type FilterRow = {
  id: string
  field: string
  type: string
  filter: string
  value: string
  customDate?: string
}

type BuilderFormValues = {
  title: string
  description: string
  platformType: number
  filteredToRecipients?: string
}

type ScheduleFormValues = {
  schedule: string
  frequency?: string
  hour: string
  minute: string
  period: 'AM' | 'PM'
  fileType: string
  companyId?: string
  subject?: string
  body?: string
}

type ReportBuilderScheduleEditPayload = {
  time?: string
  scheduleTime?: string
  schedule?: string
  fileType?: string
  subject?: string
  body?: string
  companyId?: string
  recipients?: Array<{ id: string; type?: number }>
  cc?: string[]
}

type FilterCompatibilityRule = {
  type: string
  value: string
  allowedOperators: Set<string>
  warning: string
}

type FilterValidationControl = 'operator' | 'value' | 'customDate'

type FilterValidationIssue = {
  filterId: string
  control: FilterValidationControl
  message: string
}

type InvalidFilterControls = Record<string, Partial<Record<FilterValidationControl, true>>>

const SCHEDULE_OPTIONS = [
  { value: 'D', label: 'Daily' },
  { value: 'W', label: 'Weekly' },
  { value: 'M', label: 'Monthly' },
  { value: 'NOW', label: 'Now' },
]

const WEEKLY_OPTIONS = [
  { value: 'Mon', label: 'Monday' },
  { value: 'Tue', label: 'Tuesday' },
  { value: 'Wed', label: 'Wednesday' },
  { value: 'Thu', label: 'Thursday' },
  { value: 'Fri', label: 'Friday' },
  { value: 'Sat', label: 'Saturday' },
  { value: 'Sun', label: 'Sunday' },
]

const MONTHLY_OPTIONS = Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))
const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const value = String(i + 1).padStart(2, '0')
  return { value, label: value }
})
const MINUTE_OPTIONS = ['00', '15', '30', '45'].map((value) => ({ value, label: value }))
const PERIOD_OPTIONS = [
  { value: 'AM', label: 'AM' },
  { value: 'PM', label: 'PM' },
]
const DEFAULT_SCHEDULE_FILE_TYPE = 'xlsx'
const FILE_TYPE_OPTIONS = [{ value: 'xlsx', label: 'Excel' }]

const FILTERED_TO_FALLBACK = 'Firm-Wide'
const FORTNIGHT_DATE_VALUE = 'fortnight'
const FORTNIGHT_ALLOWED_OPERATORS = new Set(['==', '>', '<'])
const FORTNIGHT_OPERATOR_WARNING = 'Fortnight can only be used with is, is after, or is before.'
const FILTER_COMPATIBILITY_RULES: FilterCompatibilityRule[] = [
  {
    type: 'date',
    value: FORTNIGHT_DATE_VALUE,
    allowedOperators: FORTNIGHT_ALLOWED_OPERATORS,
    warning: FORTNIGHT_OPERATOR_WARNING,
  },
]
const reportTitleSchema = entityNameSchema('Title')
const reportDescriptionSchema = longTextSchema('Description')
const subjectSchema = shortTextSchema('Subject', { required: false })
const bodySchema = longTextSchema('Body')

function normalizeScheduleFileType(fileType?: string) {
  return String(fileType || '').toLowerCase() === DEFAULT_SCHEDULE_FILE_TYPE ? DEFAULT_SCHEDULE_FILE_TYPE : DEFAULT_SCHEDULE_FILE_TYPE
}

function hasCustomSelections(
  selectedDimensions: string[],
  selectedMeasures: string[],
  fieldDimensionOptions: FieldOption[],
  fieldMeasureOptions: FieldOption[]
) {
  const customDimensionValues = fieldDimensionOptions.filter((option) => option.isCustom).map((option) => option.value)
  const customMeasureValues = fieldMeasureOptions.filter((option) => option.isCustom).map((option) => option.value)

  return (
    selectedDimensions.some((value) => customDimensionValues.includes(value)) ||
    selectedMeasures.some((value) => customMeasureValues.includes(value))
  )
}

function isSpecificDateValue(value: string) {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/
  if (!dateRegex.test(value)) return false
  const date = new Date(value)
  return !Number.isNaN(date.getTime())
}

function isCustomDateSelection(value?: string) {
  return value === 'Date' || value === 'custom'
}

function getFilterCompatibilityRule(type?: string, value?: string) {
  const normalizedType = String(type || '').toLowerCase()
  const normalizedValue = String(value || '').toLowerCase()
  return FILTER_COMPATIBILITY_RULES.find(
    (rule) => rule.type.toLowerCase() === normalizedType && rule.value.toLowerCase() === normalizedValue
  )
}

function isFilterOperatorAllowedForValue(type?: string, value?: string, operator?: string) {
  const rule = getFilterCompatibilityRule(type, value)
  return !rule || rule.allowedOperators.has(String(operator || ''))
}

function getFilterValueCompatibilityWarning(type?: string, value?: string, operator?: string) {
  const rule = getFilterCompatibilityRule(type, value)
  if (!rule || rule.allowedOperators.has(String(operator || ''))) return null
  return rule.warning
}

function getAllowedFilterOperatorsForValue<T extends { value: string }>(type: string, value: string, options: T[]) {
  const rule = getFilterCompatibilityRule(type, value)
  if (!rule) return options
  return options.filter((option) => rule.allowedOperators.has(String(option.value)))
}

function doesFilterTypeUseOperatorAsValue(type?: string) {
  return type === 'boolean' || type === 'booleanString'
}

function canEditFilterValue(row: FilterRow) {
  return doesFilterTypeUseOperatorAsValue(row.type) || Boolean(row.filter)
}

function formatDateValue(dateValue?: string) {
  if (!dateValue) return ''
  const date = new Date(dateValue)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 10)
}

export default function ReportBuilderPage() {
  const session = useAuthSession()
  const { hasAnyPermission } = useAuthorization()
  const { addToast } = useToast()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { reportBuilderId, scheduleId } = useParams<{ reportBuilderId?: string; scheduleId?: string }>()

  const clientId = session?.currentClientId || ''
  const isDiAdmin = session?.currentUserTypeId === UserTypes.Admin
  const isGroup = searchParams.get('type') === 'group'
  const isEdit = Boolean(reportBuilderId)
  const isEditSchedule = Boolean(scheduleId)

  const [form] = Form.useForm<BuilderFormValues>()
  const [scheduleForm] = Form.useForm<ScheduleFormValues>()

  const [selectedDimensions, setSelectedDimensions] = useState<string[]>([])
  const [selectedMeasures, setSelectedMeasures] = useState<string[]>([])
  const [selectedFilters, setSelectedFilters] = useState<FilterRow[]>([])
  const [invalidFilterControls, setInvalidFilterControls] = useState<InvalidFilterControls>({})
  const [pendingFilterField, setPendingFilterField] = useState<string | undefined>(undefined)
  const [selectedUsers, setSelectedUsers] = useState<Array<{ id: string; name: string; type: number }>>([])
  const [selectedUserGroups, setSelectedUserGroups] = useState<Array<{ id: string; name: string; type: number }>>([])
  const [selectedCc, setSelectedCc] = useState<Array<{ id: string; name: string; type: number }>>([])
  const [isTemplate, setIsTemplate] = useState(false)
  const [hasSaved, setHasSaved] = useState(false)
  const [isPreview, setIsPreview] = useState(false)
  const [previewRows, setPreviewRows] = useState<Record<string, unknown>[]>([])
  const [previewReportFileId, setPreviewReportFileId] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const [previewExportPayload, setPreviewExportPayload] = useState<Awaited<ReturnType<typeof buildExportPayload>> | null>(null)

  const reportQuery = useReportBuilder(isGroup ? '' : reportBuilderId || '')
  const groupHomeQuery = useReportBuilderHomeList(clientId, false)
  const scheduleQuery = useReportBuilderSchedule(scheduleId || '')
  const platformTypesQuery = useAvailableReportPlatformTypes(clientId)
  const platformType = Form.useWatch('platformType', form) || 2
  const scheduleType = Form.useWatch('schedule', scheduleForm)

  const optionsQuery = useReportBuilderOptions(clientId, Number(platformType))
  const recipientsQuery = useRecipients(clientId)

  const saveReportMutation = useSaveReportBuilderMutation()
  const saveScheduleMutation = useSaveReportBuilderScheduleMutation()
  const deleteReportMutation = useDeleteReportBuilderMutation()
  const deleteScheduleMutation = useDeleteReportBuilderScheduleMutation()
  const generatePreviewMutation = useGenerateReportBuilderPreviewMutation()
  const generateDownloadMutation = useGenerateReportBuilderDownloadMutation()

  const canEdit = useMemo(() => {
    if (!isEdit) return true
    return (isTemplate && isDiAdmin) || !isTemplate
  }, [isEdit, isTemplate, isDiAdmin])
  const canMutateReport = useMemo(
    () => hasAnyPermission([permissions.addReport, permissions.editReport, permissions.updateNotification]),
    [hasAnyPermission]
  )
  const canMutateSchedule = useMemo(
    () =>
      hasAnyPermission([
        permissions.addscheduledreport,
        permissions.editschedulereport,
        permissions.getscheduledreport,
        permissions.updateNotification,
      ]),
    [hasAnyPermission]
  )
  const showPrimarySaveButton = useMemo(() => {
    if (isGroup || isEditSchedule) return true
    return canEdit
  }, [canEdit, isEditSchedule, isGroup])

  const allFieldOptions = useMemo(() => {
    const options = optionsQuery.data
    if (!options) return []
    return [...options.fieldDimensionOptions, ...options.fieldMeasureOptions]
  }, [optionsQuery.data])

  const recipientOptions = useMemo(() => {
    const all = recipientsQuery.data || []
    return {
      users: all.filter((item) => item.type === 1),
      groups: all.filter((item) => item.type === 2),
    }
  }, [recipientsQuery.data])

  const filteredToRecipientsOptions = useMemo(() => {
    return [
      { value: FILTERED_TO_FALLBACK, label: FILTERED_TO_FALLBACK },
      ...selectedDimensions.map((dimension) => ({ value: dimension, label: dimension })),
    ]
  }, [selectedDimensions])

  const getFilterFieldLabel = (filter: FilterRow) => allFieldOptions.find((item) => item.value === filter.field)?.name || filter.field || 'filter'

  const getFilterValidationIssue = (filter: FilterRow): FilterValidationIssue | null => {
    const fieldLabel = getFilterFieldLabel(filter)

    if (!filter.field || !filter.filter) {
      return {
        filterId: filter.id,
        control: 'operator',
        message: `Select an operator for "${fieldLabel}".`,
      }
    }

    const compatibilityWarning = getFilterValueCompatibilityWarning(filter.type, filter.value, filter.filter)
    if (compatibilityWarning) {
      return {
        filterId: filter.id,
        control: 'operator',
        message: compatibilityWarning,
      }
    }

    if (doesFilterTypeUseOperatorAsValue(filter.type)) return null

    if (filter.type === 'date' && isCustomDateSelection(filter.value)) {
      return formatDateValue(filter.customDate)
        ? null
        : {
            filterId: filter.id,
            control: 'customDate',
            message: `Select a custom date for "${fieldLabel}".`,
          }
    }

    if (!String(filter.value || '').trim()) {
      return {
        filterId: filter.id,
        control: 'value',
        message: filter.type === 'date' ? `Select a value for "${fieldLabel}".` : `Enter a value for "${fieldLabel}".`,
      }
    }

    return null
  }

  const getFirstFilterValidationIssue = (filters: FilterRow[]) => {
    for (const filter of filters) {
      const issue = getFilterValidationIssue(filter)
      if (issue) return issue
    }
    return null
  }

  const setFilterValidationIssue = (issue: FilterValidationIssue) => {
    setInvalidFilterControls({ [issue.filterId]: { [issue.control]: true } })
  }

  const clearFilterValidationIssue = (filterId: string, control: FilterValidationControl) => {
    setInvalidFilterControls((prev) => {
      const rowControls = prev[filterId]
      if (!rowControls?.[control]) return prev
      const nextRowControls = { ...rowControls }
      delete nextRowControls[control]
      const next = { ...prev }
      if (Object.keys(nextRowControls).length) {
        next[filterId] = nextRowControls
      } else {
        delete next[filterId]
      }
      return next
    })
  }

  const isFilterControlInvalid = (filterId: string, control: FilterValidationControl) => Boolean(invalidFilterControls[filterId]?.[control])

  const platformTypeOptions = useMemo(() => {
    const fallbackOptions = PLATFORM_TYPE_OPTIONS.map((item) => ({ value: Number(item.value), label: item.label }))
    const fetchedOptions = (platformTypesQuery.data || [])
      .map((item) => ({
        value: Number(item.value),
        label: String(item.name || '').trim(),
      }))
      .filter((item) => Number.isFinite(item.value) && item.label)

    const merged = new Map<number, string>()
    fallbackOptions.forEach((item) => merged.set(item.value, item.label))
    fetchedOptions.forEach((item) => merged.set(item.value, item.label))

    return Array.from(merged, ([value, label]) => ({ value, label }))
  }, [platformTypesQuery.data])

  const hasValidFilterState = useMemo(
    () =>
      selectedFilters.every((filter) => {
        if (!filter.field || !filter.filter) return false
        if (!isFilterOperatorAllowedForValue(filter.type, filter.value, filter.filter)) return false
        if (doesFilterTypeUseOperatorAsValue(filter.type)) return true
        if (filter.type === 'date' && isCustomDateSelection(filter.value)) return Boolean(formatDateValue(filter.customDate))
        if (filter.type === 'date') return Boolean(String(filter.value || '').trim())
        return Boolean(String(filter.value || '').trim())
      }),
    [selectedFilters]
  )

  const hasBuilderMinimumValidState = useMemo(() => {
    const title = String(form.getFieldValue('title') || '').trim()
    const hasFields = selectedDimensions.length > 0 || selectedMeasures.length > 0
    return Boolean(title && hasFields && hasValidFilterState)
  }, [form, selectedDimensions.length, selectedMeasures.length, hasValidFilterState])

  const hasScheduleMinimumValidState = useMemo(() => {
    if (!isEditSchedule) return false
    const fileType = String(scheduleForm.getFieldValue('fileType') || '').trim()
    const schedule = String(scheduleForm.getFieldValue('schedule') || '').trim()
    if (!fileType || !schedule) return false
    if ((schedule === 'W' || schedule === 'M') && !String(scheduleForm.getFieldValue('frequency') || '').trim()) return false
    if (schedule !== 'NOW') {
      const hour = String(scheduleForm.getFieldValue('hour') || '').trim()
      const minute = String(scheduleForm.getFieldValue('minute') || '').trim()
      const period = String(scheduleForm.getFieldValue('period') || '').trim()
      if (!hour || !minute || !period) return false
    }
    const recipientCount = selectedUsers.length + selectedUserGroups.length
    if (!recipientCount) return false
    return true
  }, [scheduleForm, isEditSchedule, selectedUsers.length, selectedUserGroups.length])

  const hasUnsavedActionableChanges = useMemo(() => {
    if (hasSaved) return false
    const builderTouched =
      form.isFieldsTouched() || selectedDimensions.length > 0 || selectedMeasures.length > 0 || selectedFilters.length > 0
    const scheduleTouched =
      isEditSchedule &&
      (scheduleForm.isFieldsTouched() || selectedUsers.length > 0 || selectedUserGroups.length > 0 || selectedCc.length > 0)
    const hasActionableValidState = hasBuilderMinimumValidState || hasScheduleMinimumValidState
    return (builderTouched || scheduleTouched) && hasActionableValidState
  }, [
    hasSaved,
    form,
    scheduleForm,
    selectedDimensions.length,
    selectedMeasures.length,
    selectedFilters.length,
    selectedUsers.length,
    selectedUserGroups.length,
    selectedCc.length,
    isEditSchedule,
    hasBuilderMinimumValidState,
    hasScheduleMinimumValidState,
  ])

  useBeforeUnload(
    (event) => {
      if (!hasUnsavedActionableChanges) return
      event.preventDefault()
      event.returnValue = ''
    },
    { capture: true }
  )

  const currentGroup = useMemo(() => {
    if (!isGroup || !reportBuilderId) return null
    return (groupHomeQuery.data || []).find((group) => group.reportBuilderGroupId === reportBuilderId) || null
  }, [groupHomeQuery.data, isGroup, reportBuilderId])

  useEffect(() => {
    if (isGroup || !isEdit || !reportQuery.data) return

    const report = reportQuery.data
    setIsTemplate(report.isTemplate)
    form.setFieldsValue({
      title: report.title,
      description: report.description,
      platformType: report.platformType,
      filteredToRecipients: report.filteredToRecipients || FILTERED_TO_FALLBACK,
    })
    setSelectedDimensions(report.dimensions || [])
    setSelectedMeasures(report.measures || [])
    setSelectedFilters(
      (report.filters || []).map((filter, index) => {
        const rawValue = String(filter.value ?? '')
        const isCustomDate = filter.type === 'date' && isSpecificDateValue(rawValue)
        return {
          id: `${index + 1}-${filter.field}`,
          field: filter.field,
          type: filter.type,
          filter: doesFilterTypeUseOperatorAsValue(filter.type) ? String(filter.filter || rawValue) : String(filter.filter || ''),
          value: doesFilterTypeUseOperatorAsValue(filter.type) ? '' : isCustomDate ? 'custom' : rawValue,
          customDate: isCustomDate ? rawValue : undefined,
        }
      })
    )
  }, [isEdit, isGroup, reportQuery.data, form])

  useEffect(() => {
    if (!isEditSchedule || !scheduleQuery.data) return

    const payload = scheduleQuery.data as ReportBuilderScheduleEditPayload
    const time = revertUtcTime(String(payload.time || payload.scheduleTime || '08:00'))
    const rawSchedule = String(payload.schedule || '')
    const [scheduleTypePart, scheduleFrequencyPart] = rawSchedule.includes('-') ? rawSchedule.split('-') : [rawSchedule, '']

    scheduleForm.setFieldsValue({
      schedule: scheduleTypePart || 'D',
      frequency: scheduleFrequencyPart || undefined,
      hour: time.hour,
      minute: time.minute,
      period: time.period,
      fileType: normalizeScheduleFileType(String(payload.fileType || '')),
      subject: String(payload.subject || ''),
      body: String(payload.body || ''),
      companyId: String(payload.companyId || ''),
    })

    const recipients = Array.isArray(payload.recipients) ? payload.recipients : []
    const users = recipientOptions.users.filter((candidate) => recipients.some((item: { id: string }) => item.id === candidate.id))
    const groups = recipientOptions.groups.filter((candidate) => recipients.some((item: { id: string }) => item.id === candidate.id))
    const ccList: string[] = Array.isArray(payload.cc) ? payload.cc : []
    const cc = recipientOptions.users.filter((candidate) => ccList.includes(candidate.id))

    setSelectedUsers(users)
    setSelectedUserGroups(groups)
    setSelectedCc(cc)
  }, [isEditSchedule, scheduleQuery.data, scheduleForm, recipientOptions.groups, recipientOptions.users])

  const addFilter = (fieldName: string) => {
    const field = allFieldOptions.find((item) => item.value === fieldName)
    if (!field) return
    setSelectedFilters((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${fieldName}`,
        field: field.value,
        type: field.type,
        filter: '',
        value: '',
      },
    ])
    setPendingFilterField(undefined)
  }

  const removeFilter = (id: string) => {
    setSelectedFilters((prev) => prev.filter((item) => item.id !== id))
    setInvalidFilterControls((prev) => {
      if (!prev[id]) return prev
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  const mapFiltersForApi = () =>
    selectedFilters.map((filter) => ({
      field: filter.field,
      filter: filter.filter,
      type: filter.type,
      value: filter.type === 'date' && isCustomDateSelection(filter.value) ? formatDateValue(filter.customDate) : filter.value,
    }))

  const validateBuilder = async () => {
    const values = await form.validateFields()

    if (!selectedDimensions.length && !selectedMeasures.length) {
      throw new Error('Select at least one dimension or measure.')
    }

    if (isDiAdmin && !String(values.description || '').trim()) {
      throw new Error('Description is required for DI admin.')
    }

    if (
      isDiAdmin &&
      values.title &&
      (isTemplate || !isEdit) &&
      hasCustomSelections(
        selectedDimensions,
        selectedMeasures,
        optionsQuery.data?.fieldDimensionOptions || [],
        optionsQuery.data?.fieldMeasureOptions || []
      )
    ) {
      throw new Error('Cannot use custom measures/dimensions on a template.')
    }

    const filterValidationIssue = getFirstFilterValidationIssue(selectedFilters)
    if (filterValidationIssue) {
      setFilterValidationIssue(filterValidationIssue)
      throw new Error(filterValidationIssue.message)
    }

    return values
  }

  const validatePreviewBuilder = async () => {
    const values = await form.validateFields()

    if (!String(values.title || '').trim()) {
      throw new Error('Report title is missing.')
    }

    if (!selectedDimensions.length && !selectedMeasures.length) {
      throw new Error('Select at least one dimension or measure.')
    }

    const filterValidationIssue = getFirstFilterValidationIssue(selectedFilters)
    if (filterValidationIssue) {
      setFilterValidationIssue(filterValidationIssue)
      throw new Error(filterValidationIssue.message)
    }

    return values
  }

  const validateSchedule = async () => {
    const values = await scheduleForm.validateFields()
    const recipientCount = selectedUsers.length + selectedUserGroups.length
    if (!recipientCount) {
      throw new Error('Select at least one recipient from users or user groups.')
    }
    return values
  }

  const buildPreviewPayload = async (): Promise<ReportBuilderPreviewRequest> => {
    const builder = await validatePreviewBuilder()
    return {
      clientId,
      platformType: Number(builder.platformType),
      dimensions: selectedDimensions,
      measures: selectedMeasures,
      filters: mapFiltersForApi(),
      filteredByRecipients: selectedDimensions.includes(String(builder.filteredToRecipients || ''))
        ? String(builder.filteredToRecipients)
        : null,
    }
  }

  const buildExportPayload = async () => {
    const builder = await validatePreviewBuilder()
    return {
      clientId,
      platformType: Number(builder.platformType),
      dimensions: selectedDimensions,
      measures: selectedMeasures,
      filters: mapFiltersForApi(),
      filteredByRecipients: selectedDimensions.includes(String(builder.filteredToRecipients || ''))
        ? String(builder.filteredToRecipients)
        : null,
      reportTitle: String(builder.title || 'report'),
      fileType: DEFAULT_SCHEDULE_FILE_TYPE,
    }
  }

  const save = async (forCurrentClient = false) => {
    try {
      const builder = isEditSchedule || isGroup ? null : await validateBuilder()
      const scheduleValues = isEditSchedule ? await validateSchedule() : null

      const filteredToRecipients =
        builder && selectedDimensions.includes(String(builder.filteredToRecipients || ''))
          ? String(builder.filteredToRecipients)
          : null

      let savedReportId = reportBuilderId || ''
      if (!isEditSchedule && !isGroup) {
        const reportPayload: ReportBuilder = {
          reportBuilderId: isEdit ? reportBuilderId || '' : '00000000-0000-0000-0000-000000000000',
          clientId: isDiAdmin && !forCurrentClient && !isEdit ? null : clientId,
          title: String(builder?.title || ''),
          description: String(builder?.description || ''),
          platformType: Number(builder?.platformType || 0),
          dimensions: selectedDimensions,
          measures: selectedMeasures,
          filters: mapFiltersForApi(),
          filteredToRecipients,
          isTemplate: isEdit ? isTemplate : isDiAdmin && !forCurrentClient,
        }

        const nextReportId = await saveReportMutation.mutateAsync({ reportBuilder: reportPayload })
        if (!nextReportId) {
          throw new Error('Failed to save report.')
        }
        savedReportId = String(nextReportId)
      }

      if (isEditSchedule && scheduleValues) {
        const scheduleCode =
          scheduleValues.schedule === 'W' || scheduleValues.schedule === 'M'
            ? `${scheduleValues.schedule}-${scheduleValues.frequency}`
            : scheduleValues.schedule

        const schedulePayload: ReportBuilderSchedule = {
          reportBuilderScheduleId: scheduleId || '00000000-0000-0000-0000-000000000000',
          reportBuilderId: isGroup ? '00000000-0000-0000-0000-000000000000' : savedReportId,
          reportBuilderGroupId: isGroup ? reportBuilderId || '' : '00000000-0000-0000-0000-000000000000',
          clientId,
          schedule: scheduleCode,
          companyId: String(scheduleValues.companyId || ''),
          scheduleTime: getSelectedUtcTime(scheduleValues.hour, scheduleValues.minute, scheduleValues.period),
          fileType: scheduleValues.fileType,
          cc: selectedCc.map((value) => value.id),
          subject: String(scheduleValues.subject || ''),
          body: String(scheduleValues.body || ''),
          recipients: [...selectedUsers, ...selectedUserGroups].map((value) => ({ id: value.id, type: value.type })),
        }

        const scheduleSaved = await saveScheduleMutation.mutateAsync({ reportBuilderSchedule: schedulePayload })
        if (!scheduleSaved) {
          throw new Error('Report saved but scheduling failed.')
        }
      }

      setHasSaved(true)
      addToast(isEditSchedule ? 'Schedule saved successfully.' : 'Report saved successfully.', 'success')
      if (isGroup) {
        navigate(`/reports/schedules/${reportBuilderId}?type=group`)
      } else {
        navigate(isEditSchedule ? `/reports/custom/${savedReportId}` : '/reports/created')
      }
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Unable to save report.', 'error')
    }
  }

  const duplicate = async () => {
    if (!reportQuery.data) return

    try {
      const builder = await validateBuilder()
      const payload: ReportBuilder = {
        reportBuilderId: '00000000-0000-0000-0000-000000000000',
        clientId,
        title: String(builder.title || ''),
        description: String(builder.description || ''),
        platformType: Number(builder.platformType),
        dimensions: selectedDimensions,
        measures: selectedMeasures,
        filters: mapFiltersForApi(),
        filteredToRecipients: selectedDimensions.includes(String(builder.filteredToRecipients || ''))
          ? String(builder.filteredToRecipients)
          : null,
        isTemplate: false,
      }

      const newReportId = await saveReportMutation.mutateAsync({ reportBuilder: payload })
      if (!newReportId) throw new Error('Failed to duplicate report.')

      addToast('Report duplicated successfully.', 'success')
      navigate(`/reports/custom/${newReportId}`)
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Unable to duplicate report.', 'error')
    }
  }

  const deleteReport = () => {
    if (!reportBuilderId) return
    Modal.confirm({
      title: 'Delete report',
      content: 'Are you sure you want to delete this report?',
      okButtonProps: { danger: true },
      onOk: async () => {
        const ok = await deleteReportMutation.mutateAsync(reportBuilderId)
        if (!ok) {
          addToast('Failed to delete report.', 'error')
          return
        }
        setHasSaved(true)
        addToast('Report deleted.', 'success')
        navigate('/reports/created')
      },
    })
  }

  const deleteSchedule = () => {
    if (!scheduleId) return
    Modal.confirm({
      title: 'Delete schedule',
      content: 'Are you sure you want to delete this schedule?',
      okButtonProps: { danger: true },
      onOk: async () => {
        const ok = await deleteScheduleMutation.mutateAsync(scheduleId)
        if (!ok) {
          addToast('Failed to delete schedule.', 'error')
          return
        }
        setHasSaved(true)
        addToast('Schedule deleted.', 'success')
        navigate(isGroup ? `/reports/schedules/${reportBuilderId}?type=group` : `/reports/custom/${reportBuilderId}`)
      },
    })
  }

  const preview = async () => {
    try {
      const payload = await buildPreviewPayload()
      const exportPayload = await buildExportPayload()
      addToast('Fetching data for preview. This might take a couple of minutes. Please do not refresh the page.', 'info')
      setPreviewLoading(true)
      const result = await generatePreviewMutation.mutateAsync(payload)

      setPreviewRows(result.rows)
      setPreviewReportFileId(result.reportBuilderFileId)
      setPreviewExportPayload(exportPayload)
      setIsPreview(true)
      addToast('Preview generated successfully.', 'success')
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to generate preview.', 'error')
    } finally {
      setPreviewLoading(false)
    }
  }

  const exportReport = async () => {
    if (!isPreview || !previewReportFileId) {
      addToast('Generate preview before exporting the report.', 'warning')
      return
    }
    try {
      const payload = previewExportPayload ?? (await buildExportPayload())
      setExportLoading(true)
      const { presignedUrl, fileName } = await generateDownloadMutation.mutateAsync(payload)

      const anchor = document.createElement('a')
      anchor.href = presignedUrl
      anchor.download = fileName
      anchor.click()
      addToast('Report export started.', 'success')
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to export report.', 'error')
    } finally {
      setExportLoading(false)
    }
  }

  const isLoading = (!isGroup && reportQuery.isLoading) || (isEditSchedule && scheduleQuery.isLoading)

  const previewColumns = useMemo(() => {
    const objectWithMostKeys = previewRows.reduce(
      (max, current) => (Object.keys(current).length > Object.keys(max).length ? current : max),
      {} as Record<string, unknown>
    )

    return Object.keys(objectWithMostKeys).map((key) => ({
      title: key,
      dataIndex: key,
      key,
      render: (value: unknown) => {
        if (typeof value === 'number') {
          return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(value)
        }
        if (value == null) return ''
        if (typeof value === 'object') return JSON.stringify(value)
        return String(value)
      },
    }))
  }, [previewRows])

  const filterColumns = useMemo(
    () => [
      {
        title: 'Field',
        dataIndex: 'field',
        key: 'field',
        width: 240,
        render: (value: string) => {
          const field = allFieldOptions.find((item) => item.value === value)
          return field?.name || value
        },
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        width: 140,
      },
      {
        title: 'Operator',
        dataIndex: 'filter',
        key: 'filter',
        width: 260,
        render: (_: string, row: FilterRow) => {
          const dateConditionOptions = getAllowedFilterOperatorsForValue(
            row.type,
            row.value,
            optionsQuery.data?.dateConditionFilterOptions || []
          ).map((item) => ({ value: item.value, label: item.name || item.value }))

          return (
            <Select
              value={row.filter || undefined}
              style={{ width: '100%', minWidth: 220 }}
              status={isFilterControlInvalid(row.id, 'operator') ? 'error' : undefined}
              options={
                row.type === 'number'
                  ? (optionsQuery.data?.numberFilterOptions || []).map((item) => ({ value: item.value, label: item.name || item.value }))
                  : row.type === 'date'
                    ? dateConditionOptions
                    : row.type === 'boolean'
                      ? (optionsQuery.data?.booleanFilterOptions || []).map((item) => ({ value: item.value, label: item.name || item.value }))
                      : row.type === 'booleanString'
                        ? (optionsQuery.data?.booleanStringFilterOptions || []).map((item) => ({ value: item.value, label: item.name || item.value }))
                        : (optionsQuery.data?.stringFilterOptions || []).map((item) => ({ value: item.value, label: item.name || item.value }))
              }
              onChange={(value) => {
                clearFilterValidationIssue(row.id, 'operator')
                setSelectedFilters((prev) =>
                  prev.map((item) =>
                    item.id === row.id
                      ? {
                          ...item,
                          filter: String(value),
                          value: doesFilterTypeUseOperatorAsValue(item.type) ? '' : item.value,
                        }
                      : item
                  )
                )
              }}
              disabled={!canEdit || isEditSchedule || !canMutateReport}
            />
          )
        },
      },
      {
        title: 'Value',
        dataIndex: 'value',
        key: 'value',
        width: 340,
        render: (_: string, row: FilterRow) => {
          const isValueDisabled = !canEdit || isEditSchedule || !canMutateReport || !canEditFilterValue(row)

          if (doesFilterTypeUseOperatorAsValue(row.type)) {
            return <Typography.Text type="secondary">-</Typography.Text>
          }

          if (row.type === 'date') {
            const apiDateOptions = (optionsQuery.data?.dateFreqFilterOptions || []).map((item) => ({
              value: item.value,
              label: item.name || item.value,
            }))
            const hasCustomDateOption = apiDateOptions.some((item) => isCustomDateSelection(String(item.value)))
            const dateValueOptions = hasCustomDateOption ? apiDateOptions : [...apiDateOptions, { value: 'custom', label: 'Date' }]
            return (
              <Space direction="vertical" size={6} className="w-full">
                <Select
                  value={row.value || undefined}
                  style={{ width: '100%', minWidth: 220 }}
                  status={isFilterControlInvalid(row.id, 'value') ? 'error' : undefined}
                  options={dateValueOptions}
                  onChange={(value) => {
                    const nextValue = String(value)
                    const compatibilityWarning = getFilterValueCompatibilityWarning(row.type, nextValue, row.filter)
                    if (compatibilityWarning) {
                      setFilterValidationIssue({ filterId: row.id, control: 'operator', message: compatibilityWarning })
                      addToast(compatibilityWarning, 'warning')
                      return
                    }

                    clearFilterValidationIssue(row.id, 'value')
                    setSelectedFilters((prev) =>
                      prev.map((item) =>
                        item.id === row.id
                          ? {
                              ...item,
                              value: nextValue,
                              customDate: isCustomDateSelection(nextValue) ? item.customDate : undefined,
                            }
                          : item
                      )
                    )
                  }}
                  disabled={isValueDisabled}
                />
                {isCustomDateSelection(row.value) ? (
                  <Input
                    type="date"
                    style={{ width: '100%', minWidth: 220 }}
                    status={isFilterControlInvalid(row.id, 'customDate') ? 'error' : undefined}
                    value={formatDateValue(row.customDate)}
                    onChange={(event) => {
                      clearFilterValidationIssue(row.id, 'customDate')
                      setSelectedFilters((prev) =>
                        prev.map((item) => (item.id === row.id ? { ...item, customDate: event.target.value } : item))
                      )
                    }}
                    disabled={isValueDisabled}
                  />
                ) : null}
              </Space>
            )
          }

          return (
            <Input
              value={row.value}
              style={{ width: '100%', minWidth: 220 }}
              status={isFilterControlInvalid(row.id, 'value') ? 'error' : undefined}
              onChange={(event) => {
                const value = event.target.value
                clearFilterValidationIssue(row.id, 'value')
                setSelectedFilters((prev) => prev.map((item) => (item.id === row.id ? { ...item, value } : item)))
              }}
              disabled={isValueDisabled}
            />
          )
        },
      },
      {
        title: '',
        key: 'actions',
        width: 60,
        render: (_: unknown, row: FilterRow) => (
          <Button
            danger
            type="text"
            icon={<DeleteOutlined />}
            aria-label="Remove filter"
            onClick={() => removeFilter(row.id)}
            disabled={!canEdit || isEditSchedule || !canMutateReport}
          />
        ),
      },
    ],
    [addToast, allFieldOptions, canEdit, isEditSchedule, canMutateReport, optionsQuery.data]
  )

  return (
    <Space direction="vertical" size={16} className="w-full">
      <Card>
        <Space className="w-full justify-between" align="start">
          <div>
            <Typography.Title level={3} className="!mb-1">
              {isPreview
                ? 'Preview PracIntel Report'
                : isGroup && isEditSchedule
                  ? 'Edit Group Schedule'
                  : isEditSchedule
                    ? 'Edit PracIntel Schedule'
                    : isEdit
                      ? 'Edit PracIntel Report'
                      : 'Create New PracIntel Report'}
            </Typography.Title>
            <Typography.Text type="secondary">Current client: {session?.currentClientName || 'Unknown'}</Typography.Text>
          </div>
          <Space>
            {isPreview ? (
              <Button
                onClick={() => {
                  setIsPreview(false)
                  setPreviewReportFileId(null)
                }}
              >
                Continue {isEdit ? 'Editing' : 'Creating'} Report
              </Button>
            ) : null}
          </Space>
        </Space>
      </Card>

      {isLoading ? (
        <Card loading />
      ) : null}

      {!isLoading && !isGroup && reportQuery.isError ? (
        <Alert type="error" showIcon message="Failed to load report" description={reportQuery.error instanceof Error ? reportQuery.error.message : 'Unknown error'} />
      ) : null}

      {!isLoading && !isGroup && isEdit && !reportQuery.data ? <Empty description="Report not found" /> : null}
      {!isLoading && isGroup && !isEditSchedule ? <Empty description="Group editing is available from the group schedules page." /> : null}

      {!isLoading && !isPreview && !isGroup ? (
        <Card>
          <Form form={form} layout="vertical" initialValues={{ platformType: 2, filteredToRecipients: FILTERED_TO_FALLBACK }}>
            <Typography.Title level={5}>1. Report</Typography.Title>
            <Form.Item name="title" label="Title" rules={[{ validator: zodAntdValidator(reportTitleSchema) }]}>
              <Input disabled={isEditSchedule || !canEdit || !canMutateReport} />
            </Form.Item>
            <Form.Item
              name="description"
              label="Description"
              rules={[
                ...(isDiAdmin ? [{ required: true, message: 'Description is required for DI admin.' }] : []),
                { validator: zodAntdValidator(reportDescriptionSchema) },
              ]}
            >
              <Input.TextArea rows={3} disabled={isEditSchedule || !canEdit || !canMutateReport} />
            </Form.Item>
            <Form.Item name="platformType" label="Platform Type" rules={[{ required: true, message: 'Platform type is required' }]}>
              <Select
                disabled={isEditSchedule || !canEdit || isEdit || !canMutateReport}
                showSearch
                optionFilterProp="label"
                options={platformTypeOptions}
              />
            </Form.Item>

            {optionsQuery.isError ? (
              <Alert
                type="warning"
                showIcon
                message="Failed to load report builder field options"
                description={optionsQuery.error instanceof Error ? optionsQuery.error.message : 'Unknown error'}
              />
            ) : null}

            <Typography.Title level={5}>2. Fields Displayed</Typography.Title>
            <Form.Item label="Dimensions">
              <Select
                mode="multiple"
                showSearch
                optionFilterProp="label"
                value={selectedDimensions}
                onChange={setSelectedDimensions}
                disabled={isEditSchedule || !canEdit || !canMutateReport}
                options={(optionsQuery.data?.fieldDimensionOptions || []).map((item) => ({ value: item.value, label: item.name || item.value }))}
              />
            </Form.Item>
            <Form.Item label="Measures">
              <Select
                mode="multiple"
                showSearch
                optionFilterProp="label"
                value={selectedMeasures}
                onChange={setSelectedMeasures}
                disabled={isEditSchedule || !canEdit || !canMutateReport}
                options={(optionsQuery.data?.fieldMeasureOptions || []).map((item) => ({ value: item.value, label: item.name || item.value }))}
              />
            </Form.Item>

            <Typography.Title level={5}>3. Criteria</Typography.Title>
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.4fr)_minmax(320px,0.9fr)]">
              <Form.Item label="Add Filter Field" className="mb-0">
                <Space.Compact className="w-full">
                  <Select
                    value={pendingFilterField}
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    placeholder="Select field"
                    className="w-full"
                    disabled={isEditSchedule || !canEdit || !canMutateReport}
                    onChange={(value) => setPendingFilterField(value ? String(value) : undefined)}
                    options={allFieldOptions.map((item) => ({ value: item.value, label: item.name || item.value }))}
                  />
                  <Button
                    icon={<PlusOutlined />}
                    aria-label="Add filter row"
                    disabled={isEditSchedule || !canEdit || !canMutateReport || !pendingFilterField}
                    onClick={() => pendingFilterField && addFilter(pendingFilterField)}
                  >
                    Add
                  </Button>
                </Space.Compact>
              </Form.Item>
              <Form.Item name="filteredToRecipients" label="Filtered To Recipients" className="mb-0">
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={filteredToRecipientsOptions.map((item) => ({ value: item.value, label: item.label }))}
                  disabled={isEditSchedule || !canEdit || !canMutateReport}
                />
              </Form.Item>
            </div>

            <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200">
              <Table
                rowKey="id"
                columns={filterColumns}
                dataSource={selectedFilters}
                pagination={false}
                size="middle"
                scroll={{ x: 1040 }}
              />
            </div>
          </Form>
        </Card>
      ) : null}

      {!isLoading && !isPreview && isEditSchedule ? (
        <Card>
          <Space className="w-full justify-between pb-3" align="center">
            <Typography.Title level={5} className="!mb-0">
              {isGroup ? `Schedule Sending${currentGroup?.groupName ? ` - ${currentGroup.groupName}` : ''}` : '4. Schedule Sending'}
            </Typography.Title>
          </Space>

          <Form
            form={scheduleForm}
            layout="vertical"
            initialValues={{
              schedule: 'D',
              hour: '08',
              minute: '00',
              period: 'AM',
              fileType: DEFAULT_SCHEDULE_FILE_TYPE,
            }}
            className="pt-2"
          >
              <div className="grid grid-cols-1 gap-4 pb-4 md:grid-cols-2 xl:grid-cols-12">
                <Form.Item
                  name="fileType"
                  label="File Type"
                  rules={[{ required: true, message: 'File type is required' }]}
                  className="mb-0 xl:col-span-2"
                >
                  <Select options={FILE_TYPE_OPTIONS} disabled={!canMutateSchedule} />
                </Form.Item>
                <Form.Item
                  name="schedule"
                  label="Schedule"
                  rules={[{ required: true, message: 'Schedule is required' }]}
                  className="mb-0 xl:col-span-2"
                >
                  <Select options={SCHEDULE_OPTIONS} disabled={!canMutateSchedule} />
                </Form.Item>
                {scheduleType === 'W' ? (
                  <Form.Item
                    name="frequency"
                    label="Day of Week"
                    rules={[{ required: true, message: 'Frequency is required' }]}
                    className="mb-0 xl:col-span-2"
                  >
                    <Select options={WEEKLY_OPTIONS} disabled={!canMutateSchedule} />
                  </Form.Item>
                ) : null}
                {scheduleType === 'M' ? (
                  <Form.Item
                    name="frequency"
                    label="Day of Month"
                    rules={[{ required: true, message: 'Frequency is required' }]}
                    className="mb-0 xl:col-span-2"
                  >
                    <Select options={MONTHLY_OPTIONS} disabled={!canMutateSchedule} />
                  </Form.Item>
                ) : null}
                {scheduleType !== 'NOW' ? (
                  <>
                    <Form.Item
                      name="hour"
                      label="Hour"
                      rules={[{ required: true, message: 'Hour is required' }]}
                      className="mb-0 xl:col-span-2"
                    >
                      <Select options={HOUR_OPTIONS} disabled={!canMutateSchedule} />
                    </Form.Item>
                    <Form.Item
                      name="minute"
                      label="Minute"
                      rules={[{ required: true, message: 'Minute is required' }]}
                      className="mb-0 xl:col-span-2"
                    >
                      <Select options={MINUTE_OPTIONS} disabled={!canMutateSchedule} />
                    </Form.Item>
                    <Form.Item
                      name="period"
                      label="Period"
                      rules={[{ required: true, message: 'Period is required' }]}
                      className="mb-0 xl:col-span-2"
                    >
                      <Select options={PERIOD_OPTIONS} disabled={!canMutateSchedule} />
                    </Form.Item>
                  </>
                ) : null}
              </div>

              <Typography.Title level={5} className="pt-2">
                5. Access
              </Typography.Title>
              <Space direction="vertical" className="w-full" size={12}>
                <Form.Item label="Users" className="mb-0">
                  <Select
                    mode="multiple"
                    placeholder="Users"
                    className="w-full"
                    value={selectedUsers.map((item) => item.id)}
                    onChange={(ids) => setSelectedUsers(recipientOptions.users.filter((item) => ids.includes(item.id)))}
                    options={recipientOptions.users.map((item) => ({ value: item.id, label: item.name }))}
                    disabled={!canMutateSchedule}
                  />
                </Form.Item>
                <Form.Item label="User Groups" className="mb-0">
                  <Select
                    mode="multiple"
                    placeholder="User Groups"
                    className="w-full"
                    value={selectedUserGroups.map((item) => item.id)}
                    onChange={(ids) => setSelectedUserGroups(recipientOptions.groups.filter((item) => ids.includes(item.id)))}
                    options={recipientOptions.groups.map((item) => ({ value: item.id, label: item.name }))}
                    disabled={!canMutateSchedule}
                  />
                </Form.Item>
              </Space>

              <Typography.Title level={5} className="mt-4">
                6. E-mail
              </Typography.Title>
              <Space direction="vertical" className="w-full" size={12}>
                <Form.Item label="Cc" className="mb-0">
                  <Select
                    mode="multiple"
                    placeholder="Cc"
                    className="w-full"
                    value={selectedCc.map((item) => item.id)}
                    onChange={(ids) => setSelectedCc(recipientOptions.users.filter((item) => ids.includes(item.id)))}
                    options={recipientOptions.users.map((item) => ({ value: item.id, label: item.name }))}
                    disabled={!canMutateSchedule}
                  />
                </Form.Item>
                <Form.Item name="subject" label="Subject" rules={[{ validator: zodAntdValidator(subjectSchema) }]}>
                  <Input disabled={!canMutateSchedule} />
                </Form.Item>
                <Form.Item name="body" label="Body" rules={[{ validator: zodAntdValidator(bodySchema) }]}>
                  <Input.TextArea rows={3} disabled={!canMutateSchedule} />
                </Form.Item>
              </Space>
          </Form>
        </Card>
      ) : null}

      {!isLoading && !isPreview && (!isGroup || isEditSchedule) ? (
        <Card>
          <Space wrap>
            {isEdit && !isGroup ? <Button onClick={() => navigate('/reports/created')}>Back</Button> : null}
            {isEdit && !isGroup && (isDiAdmin || !isTemplate) ? (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={deleteReport}
                loading={deleteReportMutation.isPending}
                disabled={!canMutateReport}
              >
                Delete Report
              </Button>
            ) : null}
            {isEditSchedule ? (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={deleteSchedule}
                loading={deleteScheduleMutation.isPending}
                disabled={!canMutateSchedule}
              >
                Delete Schedule
              </Button>
            ) : null}
            {isEdit && !isGroup && isTemplate ? (
              <Button onClick={duplicate} disabled={saveReportMutation.isPending || !canMutateReport}>
                {isDiAdmin ? 'Save For Current Client' : 'Copy As New Report'}
              </Button>
            ) : null}
            {!isEdit && !isGroup && isDiAdmin ? (
              <Button onClick={() => void save(true)} disabled={saveReportMutation.isPending || !canMutateReport}>
                Save For Current Client
              </Button>
            ) : null}
            {!isGroup ? (
              <Button onClick={() => void preview()} loading={previewLoading || generatePreviewMutation.isPending}>
                Preview
              </Button>
            ) : null}
            {showPrimarySaveButton ? (
              <Button
                type="primary"
                loading={saveReportMutation.isPending || saveScheduleMutation.isPending}
                disabled={
                  isGroup
                    ? !canMutateSchedule
                    : isEditSchedule
                      ? !canMutateSchedule
                      : !canMutateReport
                }
                onClick={() => void save(false)}
              >
                {isGroup ? 'Save Group Schedule' : isEditSchedule ? 'Save Schedule' : isDiAdmin && !isEdit ? 'Save As Template' : 'Save'}
              </Button>
            ) : null}
          </Space>
        </Card>
      ) : null}

      {!isLoading && isPreview ? (
        <Card>
          <Space direction="vertical" size={16} className="w-full">
            <Space className="w-full justify-between" align="center">
              <Typography.Title level={4} className="!mb-0">
                {String(form.getFieldValue('title') || 'Preview')}
              </Typography.Title>
              <Button onClick={() => void exportReport()} loading={exportLoading || generateDownloadMutation.isPending}>
                Export
              </Button>
            </Space>

        {!previewRows.length ? (
          <Empty description="No preview data" />
        ) : (
          <Table<Record<string, unknown>>
            rowKey={(_, index) => String(index)}
            dataSource={previewRows}
            columns={previewColumns}
            scroll={{ x: true }}
            pagination={{ pageSize: 10, showSizeChanger: true }}
          />
        )}
          </Space>
        </Card>
      ) : null}
    </Space>
  )
}
